import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ophelos/ui/components/accordion';
import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent } from '@ophelos/ui/components/card';
import { Popover, PopoverAnchor, PopoverContent } from '@ophelos/ui/components/popover';
import { cn } from '@ophelos/ui/lib/utils';
import { useState } from 'react';
import { CheckCircle2, ChevronDown, TriangleAlert } from 'lucide-react';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useTranslation } from '@/hooks/useTranslation';
import type { BudgetAssessmentDto } from '@/lib/connect-api';

interface CompleteStepProps {
  assessment: BudgetAssessmentDto;
  /** The server said `refresh_required`: same summary, but it needs redoing. */
  expired: boolean;
  /** Finished just now, rather than returned to. Only the page can tell the two apart. */
  justCompleted: boolean;
  onUpdate: () => void;
  onDone: () => void;
  isBusy: boolean;
  /**
   * True when `onDone` hands the customer back to a payment plan they were configuring rather
   * than to the dashboard. Without saying so the primary CTA reads as "leave", which makes a
   * completed assessment look like a dead end mid-way through setting up a plan.
   */
  returnsToPlan?: boolean;
}

/** One answered question, as the customer typed it. */
export interface BudgetLine {
  questionKey: string;
  cents: number;
}

/**
 * The answered figures behind one or more summary rows.
 *
 * Already on the assessment, so a breakdown needs no extra read. Zero-valued answers are kept:
 * a customer checking their figures needs to see the ones they left empty, which is exactly how
 * a nil budget happens.
 */
export function budgetLinesFor(assessment: BudgetAssessmentDto, categories: string[]): BudgetLine[] {
  return assessment.answers
    .filter((a) => categories.includes(a.category) && a.amount_cents != null)
    .map((a) => ({ questionKey: a.question_key, cents: a.amount_cents as number }));
}

export function Amount({ cents, currency, className }: { cents: number; currency: string; className?: string }) {
  return (
    <span className={cn('tabular-nums', className)}>
      {/* The symbol is placed here rather than by CurrencyDisplay: left to itself it renders
          the minus after the symbol ("€-120"), and left-over is the one figure in the portal
          that goes negative. */}
      {cents < 0 ? '-' : ''}
      <CurrencyDisplay amount={Math.abs(cents) / 100} currency={currency} />
    </span>
  );
}

export function SummaryRow({
  label,
  cents,
  currency,
  emphasis = false,
  swatchClass,
  breakdown,
}: {
  label: string;
  cents: number;
  currency: string;
  emphasis?: boolean;
  /** Ties this row to its fill in the bar below, so identity is never colour alone. */
  swatchClass?: string;
  /** When given, the row opens to show the figures that were summed into it. */
  breakdown?: BudgetLine[];
}) {
  const labelClass = cn('flex items-center gap-2', emphasis ? 'text-sm font-medium' : 'text-sm text-muted-foreground');
  const amountClass = emphasis ? 'text-sm font-semibold' : 'text-sm font-medium';
  const { t } = useTranslation();

  if (!breakdown?.length) {
    return (
      <div className="flex items-center justify-between py-3">
        <span className={labelClass}>
          {swatchClass && <span className={cn('size-2 shrink-0 rounded-full', swatchClass)} aria-hidden="true" />}
          {label}
        </span>
        <Amount cents={cents} currency={currency} className={amountClass} />
      </div>
    );
  }

  // Mirrors the per-debt breakdown on the account plan tab rather than inventing a disclosure:
  // the answers are already on the assessment, and a customer who has just typed seven figures
  // wants to check them without walking back through the questions.
  return (
    <Accordion type="single" collapsible>
      <AccordionItem value={label} className="border-b-0">
        {/* The shared AccordionTrigger ships a ChevronUp and rotates it open, so closed reads as
            "up" — the opposite of every other expandable in this app (the frequency, date and
            method triggers all point down when shut). Its own icon is hidden and one pointing the
            conventional way is supplied here, rather than changing `packages/ui` for one caller. */}
        <AccordionTrigger className="py-3 hover:bg-transparent hover:no-underline [&>svg]:hidden [&[data-state=open]_[data-chevron]]:rotate-180">
          <span className="flex w-full items-center justify-between gap-2 pr-2">
            <span className={labelClass}>
              {swatchClass && <span className={cn('size-2 shrink-0 rounded-full', swatchClass)} aria-hidden="true" />}
              {label}
            </span>
            <span className="flex items-center gap-2">
              <Amount cents={cents} currency={currency} className={amountClass} />
              <ChevronDown data-chevron="" className="size-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            </span>
          </span>
        </AccordionTrigger>
        <AccordionContent className="divide-y divide-border-light pb-1">
          {breakdown.map((line) => (
            <div key={line.questionKey} className="flex items-center justify-between gap-3 py-2">
              <span className="text-xs text-muted-foreground">{t(`budgetAssessment.questions.${line.questionKey}.field`)}</span>
              <Amount cents={line.cents} currency={currency} className="text-xs text-foreground" />
            </div>
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

/**
 * One segment of the bar, with the label that names it.
 *
 * The label sits under the segment rather than inside it: the fill is the client's own
 * brand colour, and no token pairs a readable foreground with it the way
 * `--primary-foreground` pairs with `--primary`, so text on the fill would be legible
 * for one client's brand and not the next.
 *
 * Popover rather than Tooltip because Radix tooltips deliberately never open on touch.
 * Opening on hover as well as press gives the same affordance on a mouse, and the button
 * keeps it reachable by keyboard.
 */
export function BarSegment({
  share,
  fillClass,
  label,
  cents,
  currency,
}: {
  share: number;
  fillClass: string;
  label: string;
  cents: number;
  currency: string;
}) {
  const [open, setOpen] = useState(false);
  const amount = <CurrencyDisplay amount={cents / 100} currency={currency} className="gap-0" />;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* Open state is driven here rather than by a Trigger: a Radix tooltip never opens
          on touch, and a Popover trigger's own click-to-toggle fought the hover handlers,
          shutting the detail on the same gesture that opened it. Pointer type decides —
          a mouse gets hover, a finger gets tap-to-toggle — and focus covers the keyboard. */}
      <PopoverAnchor asChild>
        <button
          type="button"
          data-ba-segment=""
          aria-label={label}
          className="min-w-2 shrink-0 text-left"
          style={{ width: `${share}%` }}
          onPointerEnter={(e) => e.pointerType === 'mouse' && setOpen(true)}
          onPointerLeave={(e) => e.pointerType === 'mouse' && setOpen(false)}
          onPointerUp={(e) => e.pointerType !== 'mouse' && setOpen((wasOpen) => !wasOpen)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        >
          {/* No label on the mark. A plan instalment is often a few percent of income, and no
              chart shape gives a sliver that narrow room for a name and a figure — they collided
              into "Y..Le.." / "€3€65". The rows above already pair each swatch with its name and
              amount, so they are the legend; this carries proportion, and the popover carries the
              detail. `min-w-2` keeps a tiny share visible rather than letting it vanish. */}
          <span className={cn('block h-5 transition-all', fillClass)} />
        </button>
      </PopoverAnchor>
      <PopoverContent
        side="top"
        align="center"
        className="w-auto max-w-[14rem] px-3 py-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Without a Trigger, Radix counts the press that opened this as a click outside
        // and dismisses it on the same gesture. Any press on a segment is ours.
        onPointerDownOutside={(e) => {
          if ((e.target as HTMLElement | null)?.closest?.('[data-ba-segment]')) e.preventDefault();
        }}
      >
        <p className="text-xs font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground tabular-nums">{amount}</p>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Money coming in as one track, split into what goes out and what is left.
 *
 * Chart tokens, not `--primary`: a client sets its brand colour and its button colour
 * separately, and `--primary` follows the button (EDP's is navy while its brand is
 * purple), so a bar painted with it reads as chrome rather than data. `--chart-1`
 * resolves to `var(--brand-500)`, which is the client's own brand — DESIGN.md §3 makes
 * these the charting palette, and PaymentPieChart already draws from them.
 *
 * The brand sits on what goes out, which is the larger share on most budgets, so the
 * strongest colour lands on the largest quantity; what is left over takes the neutral.
 *
 * Outgoings above income cannot be drawn as a part of a whole, so that case fills the
 * track in the destructive token and the shortfall alert below carries the explanation.
 */
export function IncomeSplitBar({
  incomeCents,
  outgoingsCents,
  planCents = 0,
  currency,
}: {
  incomeCents: number;
  outgoingsCents: number;
  /** A proposed plan instalment, drawn between what goes out and what is left. */
  planCents?: number;
  currency: string;
}) {
  const { t } = useTranslation();
  const committedCents = outgoingsCents + planCents;
  const overspent = committedCents > incomeCents;
  const leftOverCents = incomeCents - committedCents;
  const share = (cents: number) => (incomeCents > 0 ? Math.min(100, (cents / incomeCents) * 100) : cents > 0 ? 100 : 0);
  const outgoingsShare = share(outgoingsCents);
  const planShare = overspent ? Math.max(0, 100 - outgoingsShare) : share(planCents);

  return (
    <div
      className="flex w-full overflow-hidden rounded-full"
      role="group"
      aria-label={t(overspent ? 'budgetAssessment.complete.barLabelShort' : 'budgetAssessment.complete.barLabel')}
    >
      <BarSegment
        share={outgoingsShare}
        fillClass={overspent ? 'bg-destructive' : 'bg-chart-1'}
        label={t('budgetAssessment.complete.outgoingsShort')}
        cents={outgoingsCents}
        currency={currency}
      />
      {planCents > 0 && (
        <BarSegment
          share={planShare}
          fillClass={overspent ? 'bg-destructive' : 'bg-chart-2'}
          label={t('budgetAssessment.complete.planShort')}
          cents={planCents}
          currency={currency}
        />
      )}
      {!overspent && leftOverCents > 0 && (
        <BarSegment
          share={100 - outgoingsShare - planShare}
          fillClass="bg-chart-5"
          label={t('budgetAssessment.complete.leftOverShort')}
          cents={leftOverCents}
          currency={currency}
        />
      )}
    </div>
  );
}

export function CompleteStep({
  assessment,
  expired,
  justCompleted,
  onUpdate,
  onDone,
  isBusy,
  returnsToPlan = false,
}: CompleteStepProps) {
  const { t } = useTranslation();
  const { config } = useConfiguration();
  const supportUrl = config.ui.companyInfo?.supportUrl;
  const { summary, currency } = assessment;
  const shortfall = summary.monthly_disposable_income_cents < 0;

  const linesFor = (categories: string[]) => budgetLinesFor(assessment, categories);

  const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <div className="w-full stack items-center px-4">
      <div className="flex-1 w-full max-w-sm mx-auto space-y-6 pt-6">
        <div className="space-y-2">
          {expired ? (
            <h1 className="text-2xl font-bold text-foreground">{t('budgetAssessment.refresh.title')}</h1>
          ) : (
            <div className="flex items-start gap-2 min-w-0">
              <CheckCircle2 className="size-5 text-primary shrink-0 mt-1.5" />
              <h1 className="text-2xl font-bold text-foreground">
                {/* Not derivable from the row: a completed assessment always carries
                    `completed_at`, so this asked a question with one answer and the
                    returning customer was thanked for work they did weeks ago. */}
                {justCompleted
                  ? t('budgetAssessment.complete.title')
                  : t('budgetAssessment.complete.reusableTitle')}
              </h1>
            </div>
          )}
          <p className="text-sm text-muted-foreground leading-relaxed">
            {expired
              ? t('budgetAssessment.refresh.body', { date: formatDate(assessment.completed_at) })
              : t('budgetAssessment.complete.completedOn', { date: formatDate(assessment.completed_at) })}
          </p>
        </div>

        <Card>
          <CardContent className="divide-y divide-border-light">
            <SummaryRow
              label={t('budgetAssessment.complete.income')}
              cents={summary.monthly_income_cents}
              currency={currency}
              breakdown={linesFor(['income'])}
            />
            <SummaryRow
              label={t('budgetAssessment.complete.outgoings')}
              cents={summary.monthly_outgoings_cents}
              currency={currency}
              swatchClass={shortfall ? 'bg-destructive' : 'bg-chart-1'}
              breakdown={linesFor(['essential_expenditure', 'priority_debt'])}
            />
            <SummaryRow
              label={t('budgetAssessment.complete.leftOver')}
              cents={summary.monthly_disposable_income_cents}
              currency={currency}
              emphasis
              swatchClass={shortfall ? undefined : 'bg-chart-5'}
            />
            <div className="pt-4">
              <IncomeSplitBar
                incomeCents={summary.monthly_income_cents}
                outgoingsCents={summary.monthly_outgoings_cents}
                currency={currency}
              />
            </div>
          </CardContent>
        </Card>

        {shortfall && (
          <Alert variant="warning">
            <TriangleAlert className="size-4" />
            {/* The offer to talk is a whole sentence rather than a phrase inside one: a
                link spliced mid-sentence has to survive every language's word order, and
                only some clients configure a support URL to link to at all. */}
            <AlertDescription>
              {t('budgetAssessment.complete.shortfall')}{' '}
              {supportUrl ? (
                <a
                  href={supportUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium underline underline-offset-2"
                >
                  {t('budgetAssessment.complete.shortfallHelp')}
                </a>
              ) : (
                t('budgetAssessment.complete.shortfallHelp')
              )}
            </AlertDescription>
          </Alert>
        )}

        {!expired && assessment.valid_until && (
          <p className="text-xs text-muted-foreground">
            {t('budgetAssessment.complete.validUntil', { date: formatDate(assessment.valid_until) })}
          </p>
        )}
      </div>

      <BottomActionBar>
        {expired ? (
          <Button size="xl" className="w-full max-w-sm" onClick={onUpdate} disabled={isBusy}>
            {t('budgetAssessment.refresh.cta')}
          </Button>
        ) : (
          <>
            <Button size="xl" className="w-full max-w-sm" onClick={onDone} disabled={isBusy}>
              {t(returnsToPlan ? 'budgetAssessment.complete.continueToPlan' : 'budgetAssessment.complete.done')}
            </Button>
            <Button variant="ghost" size="sm" className="w-full max-w-sm" onClick={onUpdate} disabled={isBusy}>
              {t('budgetAssessment.complete.update')}
            </Button>
          </>
        )}
      </BottomActionBar>
    </div>
  );
}
